import type { Module } from 'vuex';
import { authentication } from '@contimo/api/src/api';
import { updateClientAuthToken } from '@contimo/api/src/api/client';
import type { TRootStore } from '@/store';
import { TOKEN_STORE } from '@/constants/api';
import plausible from '@/utils/plausible';
import {
  INIT_AUTH,
  INIT_USER,
  INIT_USER_INTERFACE,
  LOGIN,
  LOGOUT,
  REQUEST_PASSWORD_RESET,
  RESET_PASSWORD,
} from '../actionTypes';
import {
  SET_AUTH_LOADING,
  SET_NEXT_ROUTE,
  SET_TOKEN,
  SET_AUTH_READY,
  SET_LOGIN_LOADING,
} from '../mutationsTypes';
import {
  ACCESS_TOKEN,
  AUTH_IS_READY,
  HAS_LOGIN_ERROR,
  IS_AUTH_LOADING,
  IS_LOGIN_LOADING,
  ROUTE_AFTER_LOGIN,
} from '../gettersTypes';

export interface IAuthStoreState {
  ready: boolean;
  loading: boolean;
  loginLoading: boolean;
  loginError: boolean;
  token: string | null;
  nextRoute: string | null;
  savedLoginEmail: string | null;
}

type TAuthStore = Module<IAuthStoreState, TRootStore>;

const authStore: TAuthStore = {
  state: () => ({
    ready: false,
    loading: false,
    loginLoading: false,
    loginError: false,
    token: null,
    nextRoute: null,
    savedLoginEmail: null,
  }),

  getters: {
    [IS_AUTH_LOADING]: (state) => state.loading,
    [IS_LOGIN_LOADING]: (state) => state.loginLoading,
    [HAS_LOGIN_ERROR]: (state) => state.loginError,
    [AUTH_IS_READY]: (state) => state.ready,
    [ROUTE_AFTER_LOGIN]: (state) => state.nextRoute,
    [ACCESS_TOKEN]: (state) => state.token,
  },

  mutations: {
    [SET_TOKEN](state, token: string | null): void {
      if (token) {
        localStorage.setItem(TOKEN_STORE, token);
      } else {
        localStorage.removeItem(TOKEN_STORE);
      }
      updateClientAuthToken(token);
      state.token = token;
    },
    [SET_NEXT_ROUTE](state, route: string) {
      state.nextRoute = route;
    },
    [SET_AUTH_LOADING](state, loading: boolean) {
      state.loading = loading;
    },
    [SET_LOGIN_LOADING](state, loading: boolean) {
      state.loginLoading = loading;
    },
    [SET_AUTH_READY](state, ready: boolean) {
      setTimeout(
        () => {
          state.ready = ready;
        },
        ready ? 500 : 0,
      );
    },
  },

  actions: {
    async [INIT_AUTH]({ commit, dispatch }) {
      commit(SET_AUTH_READY, false);
      commit(SET_AUTH_LOADING, true);
      dispatch(INIT_USER_INTERFACE);
      const token = localStorage.getItem(TOKEN_STORE);
      if (token) {
        commit(SET_TOKEN, token);
        try {
          const { data } = await authentication.relogin();
          if (data.user.customerId) {
            throw new Error('Can not relogin as customer');
          }
          commit(SET_TOKEN, data.auth.token);
          await dispatch(INIT_USER, data.user);
        } catch (e) {
          commit(SET_TOKEN, null);
        }
      }
      commit(SET_AUTH_READY, true);
      commit(SET_AUTH_LOADING, false);
      setTimeout(() => {
        plausible('Relogin');
      }, 300);
    },
    async [LOGIN]({ commit, dispatch }, { email, password }) {
      commit(SET_LOGIN_LOADING, true);
      try {
        const { data } = await authentication.login(email, password);
        if (data.user.customerId) {
          throw new Error('Can not login as customer');
        }
        commit(SET_TOKEN, data.auth.token);
        await dispatch(INIT_USER, data.user);
        setTimeout(() => {
          plausible('Login');
          commit(SET_LOGIN_LOADING, false);
        }, 300);
        return true;
      } catch (e) {
        commit(SET_LOGIN_LOADING, false);
        throw e;
      }
    },
    async [LOGOUT]({ commit }, func: () => void) {
      commit(SET_NEXT_ROUTE, null);
      try {
        await authentication.logout();
        commit(SET_TOKEN, null);
      } catch (e) {
        commit(SET_TOKEN, null);
      }
      func();
      return true;
    },
    async [REQUEST_PASSWORD_RESET](_, email: string) {
      await authentication.requestPasswordReset(email);
    },
    async [RESET_PASSWORD](
      _,
      { email, signature, password }: { email: string; signature: string; password: string },
    ) {
      await authentication.resetPassword(email, signature, password);
    },
  },
};

export default authStore;
